import React, {useContext, useEffect, useState} from "react";

import {CircleFill} from "react-bootstrap-icons";

import ExecutionTreeContext from "../../Providers/ExecutionTreeContext";
import StackContext from "../../Providers/StackContext";
import StackPositionContext from "../../Providers/StackPositionContext";
import {BehavioralExecutionNode} from "./BehavioralExecutionNode/BehavioralExecutionNode";
import BehavioralExecutionTreeInstanceContext from "./BehavioralExecutionTreeInstanceContext";

import "./BehavioralExecutionTree.scss";

/**
 * Contains the execution tree.
 * @return {JSX.Element}
 */
export function BehavioralExecutionTree () {
    const {behavior, executionTree, semanticState,
        setExecutionTree, activeBehavior} = useContext(ExecutionTreeContext);
    const {stackPosition} = useContext(StackPositionContext);
    const {stacks, activeThread,
        activeAbstraction, setActiveAbstraction} = useContext(StackContext);
    const [selectedNode, setSelectedNode] = useState();
    const [executionTreeInstance, setExecutionTreeInstance] = useState();

    useEffect(() => {
        if (!(activeBehavior === null || activeBehavior === undefined)) {
            const exec = behavior[activeBehavior].execution;
            if (exec && exec.length > 0) {
                const tree = behavior[activeBehavior].execution;
                setExecutionTree(behavior[activeBehavior].execution);

                // Check if the active abstraction is in the current behavior
                let found;
                if (activeAbstraction) {
                    for (let i = 0; i < tree.length; i++) {
                        if (activeAbstraction.node === tree[i]) {
                            found = true;
                            break;
                        }
                    }
                };

                // If the active abstraction isn't in this behavior, then
                // go to the last behavior.
                if (!found) {
                    const execIndex = behavior[activeBehavior].execution.length - 1;
                    const node = behavior[activeBehavior].execution[execIndex];
                    setSelectedNode(
                        behavior[activeBehavior].execution[execIndex]
                    );
                    setActiveAbstraction({
                        index: execIndex,
                        node: node,
                    });
                    node.selected = true;
                };
            }
        }
    }, [behavior, activeBehavior]);

    // Sync selected stack with the execution tree. This is to enable
    // backwards compatibility but in the future, we can eliminate the stack.
    useEffect(() => {
        if (!stacks || stackPosition === undefined || !executionTree || !stacks[activeThread]) {
            return;
        }
        const stack = stacks[activeThread].stack;
        if (!stack?.callStack || stackPosition >= stack.callStack.length) {
            return;
        }
        const stackLevel = stack.callStack[stackPosition].position;
        for (let index = 0; index < executionTree.length; index++) {
            const node = executionTree[index];
            if (node.position === stackLevel) {
                node.selected = true;
                setSelectedNode(node);
            } else {
                node.selected = false;
            }
        }
    }, [stackPosition, stacks, executionTree]);


    /**
     * Scroll to the selected node.
     * @param {Object} node
     */
    const scrollToNode = (node) => {
        const nodeElement = document.getElementById("row-exec-" + node.index);
        if (nodeElement) {
            nodeElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    };

    /**
     * Scroll to node when selected node changes.
     */
    useEffect(() => {
        if (selectedNode) {
            scrollToNode(selectedNode);
        }
    }, [selectedNode]);

    /**
     * Render the execution tree.
     */
    const renderTree = () => {
        if (executionTree) {
            const execution = [];
            let collapsedLevel;
            let collapsing = false;

            for (let index = 0; index < executionTree.length; index++) {
                const node = executionTree[index];
                node.index = index;

                // If we are collapsing and we reached the same
                // level or below, then stop collapsing.
                if (collapsing && node.level <= collapsedLevel) {
                    collapsing = false;
                }

                // If the node is collapsed and we aren't collapsing
                // then start collapsing
                if (node.collapsed && !collapsing) {
                    collapsedLevel = node.level;
                    collapsing = true;
                    execution.push(
                        <BehavioralExecutionNode
                            key={node.index}
                            node={node}/>
                    );
                    continue;
                }

                // If we aren't collapsing this node, then add the node.
                if (!collapsing) {
                    execution.push(<BehavioralExecutionNode
                        key={node.index}
                        node={node}/>
                    );
                }
            }
            setExecutionTreeInstance(execution);
        }
    };

    /**
     * Collapse the given node
     * @param {Object} node
     */
    const toggleCollapse = (node) => {
        node.collapsed = !node.collapsed;
        renderTree();
    };

    /**
     * Select the given node, this function is called from execution node.
     * @param {Object} selectedNode
     */
    const selectNode = (selectedNode) => {
        for (let index = 0; index < executionTree.length; index++) {
            const node = executionTree[index];
            if (node === selectedNode) {
                node.selected = true;
                setActiveAbstraction({
                    index: index,
                    node: node,
                });
                setSelectedNode(node);
            } else {
                node.selected = false;
            }
        }
    };

    /**
     * Render the tree when the execution tree changes.
     *
     * TODO: Change the way this is implemented.
     */
    useEffect(() => {
        if (executionTree) {
            renderTree();
        }
    }, [executionTree]);

    return (
        <BehavioralExecutionTreeInstanceContext.Provider
            value={{selectedNode, selectNode, toggleCollapse}}>
            <div className="treeMenuContainer">
                <div className="topContainer">
                    <div className="titleContainer">
                        {
                            semanticState === "execution" &&
                            <CircleFill className="focusIndicator"
                                title="Keyboard shortcuts focused on execution"/>
                        }
                        <span className="title">Semantic Execution Graph</span>
                    </div>
                    <div className="iconMenu">
                    </div>
                </div>
                <div className="executionTreeContainer scrollbar flex-grow-1">
                    {executionTreeInstance}
                </div>
            </div>
        </BehavioralExecutionTreeInstanceContext.Provider>
    );
}

export default BehavioralExecutionTree;
